import { ReactElement } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as TemperatureIcon } from '~/assets/img/ecoSimplified/temperatura.svg';
import { colors } from '~/styles/colors';
import {
  Card, CardTitle, FieldLabel, NativeSelect, SelectWrapper, SidePanel, TwoColumns,
} from '../ds';
import { buildMonitoredEnvironmentOptions, formatTemperatureRange, resolveMonitoredEnvironment } from '../helpers';
import { DamEcoSimplifiedDeviceInfo, DamEcoSimplifiedFormValues } from '../types';
import { AmbientTemperatureValue } from './AmbientTemperatureValue';
import { CheckboxField } from './controls';
import { ChevronDownIcon, ExternalArrowIcon } from './icons';

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  & + & {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid ${colors.stroke['soft-200']};
  }
`;

const InfoLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
  color: ${colors.text['sub-350']};

  & > svg {
    flex: none;
    height: 18px;
    width: auto;
  }
`;

const InfoValue = styled.span<{ $large?: boolean }>`
  flex: none;
  font-size: ${({ $large }) => ($large ? '18px' : '14px')};
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
  color: ${colors.text['main-900']};
`;

const SensorLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: none;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  color: ${colors.brand.primary.base};
`;

const Title = styled(CardTitle)`
  margin-bottom: 18px;
`;

const AmbienteSelect = styled(SelectWrapper)`
  margin-bottom: 18px;
`;

interface MonitoredEnvironmentSectionProps {
  damId: string;
  device: DamEcoSimplifiedDeviceInfo;
  readOnly: boolean;
}

export function MonitoredEnvironmentSection({ damId, device, readOnly }: Readonly<MonitoredEnvironmentSectionProps>): ReactElement {
  const { t } = useTranslation();
  const { control } = useFormContext<DamEcoSimplifiedFormValues>();
  const [selfReference, referenceDutId, referenceEnvironmentId] = useWatch({ control, name: ['selfReference', 'referenceDutId', 'referenceEnvironmentId'] });

  const referenceSensor = selfReference ? damId : referenceDutId;
  const environment = resolveMonitoredEnvironment({
    selfReference,
    reference: { dutId: referenceDutId, environmentId: referenceEnvironmentId },
    options: { duts: device.referenceDutOptions, environments: device.selfReferenceEnvironmentOptions },
    damLimits: device.temperatureLimits,
  });
  const temperatureSource = { selfReference, dutId: referenceDutId };
  const selectOptions = buildMonitoredEnvironmentOptions({
    selfReference,
    options: { duts: device.referenceDutOptions, environments: device.selfReferenceEnvironmentOptions },
    currentEnvironmentId: referenceEnvironmentId,
  });

  return (
    <Card data-testid="eco-simplificado-ambiente">
      <TwoColumns $left="minmax(0, 1.1fr)">
        <div>
          <Title>{t('ambienteMonitorado')}</Title>
          <FieldLabel as="label" htmlFor="eco-simplificado-ambiente">{t('ecoSimplificadoAmbiente')}</FieldLabel>
          <AmbienteSelect>
            {/* Um Controller por campo, com `key`, para o select não herdar o valor do outro modo. */}
            {selfReference ? (
              <Controller
                key="referenceEnvironmentId"
                control={control}
                name="referenceEnvironmentId"
                render={({ field }) => (
                  <NativeSelect
                    id="eco-simplificado-ambiente"
                    data-testid="eco-simplificado-ambiente-proprio"
                    value={field.value == null ? '' : String(field.value)}
                    onChange={(event) => field.onChange(event.target.value ? Number(event.target.value) : null)}
                    disabled={readOnly}
                  >
                    <option value="">{t('selecione')}</option>
                    {selectOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </NativeSelect>
                )}
              />
            ) : (
              <Controller
                key="referenceDutId"
                control={control}
                name="referenceDutId"
                render={({ field }) => (
                  <NativeSelect
                    id="eco-simplificado-ambiente"
                    data-testid="eco-simplificado-dut-referencia"
                    value={field.value ?? ''}
                    onChange={(event) => field.onChange(event.target.value || null)}
                    disabled={readOnly}
                  >
                    <option value="">{t('selecione')}</option>
                    {selectOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </NativeSelect>
                )}
              />
            )}
            <ChevronDownIcon color={colors.text['sub-350']} />
          </AmbienteSelect>
          <Controller
            control={control}
            name="selfReference"
            render={({ field }) => (
              <CheckboxField
                checked={field.value}
                onChange={field.onChange}
                label={t('ecoSimplificadoUsarSensorProprio')}
                hint={device.canSelfReference ? t('ecoSimplificadoUsarSensorProprioDica') : t('ecoSimplificadoSensorProprioIndisponivel')}
                disabled={readOnly || !device.canSelfReference}
              />
            )}
          />
        </div>
        <SidePanel>
          <InfoRow>
            <InfoLabel>{t('ecoSimplificadoTipoAmbiente')}</InfoLabel>
            <InfoValue>{environment.roomTypeName ?? '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('ecoSimplificadoSensorReferencia')}</InfoLabel>
            {referenceSensor ? (
              <SensorLink to={`/analise/dispositivo/${referenceSensor}/informacoes`} data-testid="eco-simplificado-sensor-referencia">
                {referenceSensor}
                <ExternalArrowIcon size={13} strokeWidth={2.2} />
              </SensorLink>
            ) : <InfoValue>-</InfoValue>}
          </InfoRow>
          <InfoRow>
            <InfoLabel>{t('ecoSimplificadoLimiteTemperatura')}</InfoLabel>
            <InfoValue>{formatTemperatureRange(environment.limits)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>
              <TemperatureIcon />
              {t('temperaturaAmbiente')}
            </InfoLabel>
            <InfoValue $large data-testid="eco-simplificado-temperatura-ambiente">
              <AmbientTemperatureValue key={`${selfReference}-${referenceDutId}`} damId={damId} source={temperatureSource} />
            </InfoValue>
          </InfoRow>
        </SidePanel>
      </TwoColumns>
    </Card>
  );
}
